import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useCallback, useState } from 'react';
import { TypedQueryFunction, useQuery } from 'react-query';
import { useDebounce } from 'use-debounce';

interface JMAHypocenter {
  depth: number;
  latitude: number;
  longitude: number;
  magnitude: number;
  name: string;
}

export enum DomesticTsunami {
  None = 'なし',
  Unknown = '不明',
  Checking = '調査中',
  NonEffective = '若干の海面変動が予想されるが、被害の心配なし',
  Watch = '津波注意報',
  Warning = '津波予報(種類不明)',
}

interface JMAEarthquake {
  domesticTsunami:
    | 'None'
    | 'Unknown'
    | 'Checking'
    | 'NonEffective'
    | 'Watch'
    | 'Warning';
  foreignTsunami: string;
  hypocenter: JMAHypocenter;
  maxScale: number;
  time: string;
}

interface JMAIssue {
  correct: string;
  source: string;
  time: string;
  type: string;
}

interface JMAPoint {
  addr: string;
  isArea: boolean;
  pref: string;
  scale: number;
}

export interface JMAQuake {
  code: number;
  created_at: string;
  earthquake: JMAEarthquake;
  id: string;
  issue: JMAIssue;
  points: JMAPoint[];
  time: string;
  'user-agent': string;
  ver: string;
}

const EQsKey = 'earthquakes';

const getEQs: TypedQueryFunction<JMAQuake[], [typeof EQsKey, number]> = async (
  key,
  minScale
) => {
  const today = format(new Date(), 'yyyyMMdd', { locale: ja });

  const data = await fetch(
    `https://api.p2pquake.net/v2/jma/quake?limit=50&since_date=${today}&min_scale=${
      minScale * 10
    }`
  );
  return data.json();
};

const DEFAULT_MIN_SCALE = 4;

export const useEQs = () => {
  const timeFetched = format(new Date(), 'yyyyMMdd HH:mm');

  const [isEqShow, setEqIsShow] = useState(true);
  const [minScale, setMinScale] = useState(DEFAULT_MIN_SCALE);
  const [debouncedMinScale] = useDebounce(minScale, 400);

  const { data } = useQuery(
    [EQsKey, timeFetched, debouncedMinScale],
    () => getEQs(EQsKey, debouncedMinScale),
    {
      enabled: isEqShow,
      refetchInterval: 60000, // 1000ms * 60s =  60000(1min)
    }
  );

  const toggleEqShow = useCallback(() => {
    setEqIsShow((p) => !p);
  }, []);

  const onMinScaleChange = useCallback((minScale: number) => {
    setMinScale(minScale);
  }, []);

  return {
    isEqShow,
    toggleEqShow,
    onMinScaleChange,
    minScale,
    eqData: data,
  };
};
